import * as Sentry from "@sentry/react";
import { getApiClient } from "@src/api";
import { whiteboardQueryKeys } from "@src/components/board/queries/query-keys";
import { parseWhiteboardDocumentId } from "@src/components/collaboration/document-id";
import { tryParseNavbarDocumentId } from "@src/components/collaboration/document-id/factories/parse-navbar-document";
import { graphql } from "@src/gql/gql";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";
import {
	type NavbarBranchItem,
	navbarBranchQueryKeys,
} from "../queries/fetch-ranklist";
import type { Whiteboard, WhiteboardListing } from "../schema";

function sameWhiteboardRef(candidate: string, whiteboardRef: string): boolean {
	const asBoard = tryParseNavbarDocumentId(candidate, "board");
	if (!asBoard.ok || asBoard.kind !== "board") {
		return false;
	}
	return (
		asBoard.documentId.toIdString() ===
		parseWhiteboardDocumentId(whiteboardRef).toIdString()
	);
}

const renameWhiteboardQuery = graphql(/* GraphQL */ `
	mutation renameWhiteboard($id: String!, $name: String!) {
		whiteboards {
			rename(whiteboardId: $id, name: $name) {
				status
				name
				ownerId
			}
		}
	}
`);

/**
 * Rename a whiteboard.
 *
 * @returns React Query mutation object
 */
type RenameWhiteboardContext = {
	previousNavbar: [QueryKey, NavbarBranchItem[] | undefined][];
	affectedBranchKeys: readonly (readonly unknown[])[];
	previousOwned: [QueryKey, WhiteboardListing | undefined][];
	previousShared: WhiteboardListing | undefined;
	previousWhiteboard: Whiteboard | undefined;
	targetIdStr: string;
};

export const useRenameWhiteboard = () => {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, name }: { id: string; name: string }) => {
			const whiteboardId = parseWhiteboardDocumentId(id).toIdString();
			const result = await client.mutate({
				mutation: renameWhiteboardQuery,
				variables: {
					id: whiteboardId,
					name,
				},
			});

			return result.data?.whiteboards.rename;
		},
		onMutate: async (variables): Promise<RenameWhiteboardContext> => {
			const targetIdStr = parseWhiteboardDocumentId(variables.id).toIdString();
			const { name: newName } = variables;

			await queryClient.cancelQueries({
				queryKey: navbarBranchQueryKeys.all(),
			});
			await queryClient.cancelQueries({
				queryKey: whiteboardQueryKeys.owned(),
			});
			await queryClient.cancelQueries({
				queryKey: whiteboardQueryKeys.shared(),
			});
			await queryClient.cancelQueries({
				queryKey: whiteboardQueryKeys.ofId(targetIdStr),
			});

			const previousNavbar = queryClient.getQueriesData<NavbarBranchItem[]>({
				queryKey: navbarBranchQueryKeys.all(),
			});
			const previousOwned = queryClient.getQueriesData<WhiteboardListing>({
				queryKey: whiteboardQueryKeys.owned(),
			});
			const previousShared = queryClient.getQueryData<WhiteboardListing>(
				whiteboardQueryKeys.shared(),
			);
			const previousWhiteboard = queryClient.getQueryData<Whiteboard>(
				whiteboardQueryKeys.ofId(targetIdStr),
			);

			const affectedBranchKeys = previousNavbar
				.filter(([, data]) =>
					data?.some((item) => sameWhiteboardRef(item.key, variables.id)),
				)
				.map(([queryKey]) => queryKey);

			queryClient.setQueriesData<NavbarBranchItem[]>(
				{ queryKey: navbarBranchQueryKeys.all() },
				(old) => {
					if (!old?.some((item) => sameWhiteboardRef(item.key, variables.id))) {
						return old;
					}
					return old.map((item) =>
						sameWhiteboardRef(item.key, variables.id)
							? { ...item, name: newName }
							: item,
					);
				},
			);

			queryClient.setQueriesData(
				{ queryKey: whiteboardQueryKeys.owned() },
				(old: WhiteboardListing | undefined) => {
					if (!old?.some((wb) => sameWhiteboardRef(wb.id, variables.id))) {
						return old;
					}
					return old.map((wb) =>
						sameWhiteboardRef(wb.id, variables.id)
							? { ...wb, name: newName }
							: wb,
					);
				},
			);

			queryClient.setQueryData(
				whiteboardQueryKeys.shared(),
				(old: WhiteboardListing | undefined) => {
					if (!old?.some((wb) => sameWhiteboardRef(wb.id, variables.id))) {
						return old;
					}
					return old.map((wb) =>
						sameWhiteboardRef(wb.id, variables.id)
							? { ...wb, name: newName }
							: wb,
					);
				},
			);

			if (previousWhiteboard) {
				queryClient.setQueryData(whiteboardQueryKeys.ofId(targetIdStr), {
					...previousWhiteboard,
					name: newName,
				});
			}

			return {
				previousNavbar,
				affectedBranchKeys,
				previousOwned,
				previousShared,
				previousWhiteboard,
				targetIdStr,
			};
		},
		onError: (error, _variables, context) => {
			if (context?.previousNavbar) {
				for (const [queryKey, data] of context.previousNavbar) {
					queryClient.setQueryData(queryKey, data);
				}
			}
			if (context?.previousOwned) {
				for (const [queryKey, data] of context.previousOwned) {
					queryClient.setQueryData(queryKey, data);
				}
			}
			if (context?.previousShared !== undefined) {
				queryClient.setQueryData(
					whiteboardQueryKeys.shared(),
					context.previousShared,
				);
			}
			if (context?.previousWhiteboard !== undefined) {
				queryClient.setQueryData(
					whiteboardQueryKeys.ofId(context.targetIdStr),
					context.previousWhiteboard,
				);
			}
			Sentry.captureException(error);
		},
		onSettled: (_data, _error, variables, context) => {
			for (const queryKey of context?.affectedBranchKeys ?? []) {
				queryClient.invalidateQueries({ queryKey });
			}
			queryClient.invalidateQueries({ queryKey: whiteboardQueryKeys.owned() });
			queryClient.invalidateQueries({ queryKey: whiteboardQueryKeys.shared() });
			const idStr = parseWhiteboardDocumentId(variables.id).toIdString();
			queryClient.invalidateQueries({
				queryKey: whiteboardQueryKeys.ofId(idStr),
			});
		},
	});
};
